"use client";

import { useState } from "react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Zap, CircleCheck as CheckCircle2, CircleX as XCircle, Clock, Bot, ArrowRight, ShieldCheck, Activity, Sparkles, SlidersHorizontal as Sliders, Workflow, MessageSquare as MessageSquareCheck, CalendarCheck, Database } from 'lucide-react';
export default function Automation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [activeTab, setActiveTab] = useState<"lead" | "reviews" | "crm">("lead");
  const [isAutomated, setIsAutomated] = useState<boolean>(true);
  const metrics = [{
    value: "< 45s",
    label: "AI Lead Response",
    subtext: "Down from 4.2 hrs average manual delay",
    icon: Clock
  }, {
    value: "3.8x",
    label: "Booking Conversion",
    subtext: "Instant qualification captures hot intent",
    icon: Zap
  }, {
    value: "100%",
    label: "Data & CRM Accuracy",
    subtext: "Zero lost contacts or manual entry errors",
    icon: Database
  }, {
    value: "24/7",
    label: "Autonomous Execution",
    subtext: "Continuous handling including nights & weekends",
    icon: Activity
  }];
  const workflows = [{
    id: "lead",
    title: "Instant Lead Qualifier & Booking",
    icon: Bot,
    description: "AI engages incoming leads in under 45 seconds across web chat, SMS, and form fills, qualifying intent and booking directly into your calendar.",
    manualState: {
      time: "3 to 6 Hours",
      cost: "High Staff Overhead",
      errorRate: "28% Dropped Inquiries",
      status: "Losing Leads to Faster Competitors",
      steps: ["Lead submits contact form or chat inquiry", "Form sits in email inbox waiting for manual check", "Staff calls back hours later when lead interest is cold", "Back-and-forth emails needed to confirm meeting time"]
    },
    automatedState: {
      time: "< 45 Seconds",
      cost: "Fractional Operating Cost",
      errorRate: "0% Missed Inquiries",
      status: "Instant Capture & Guaranteed Booking",
      steps: ["Lead submits inquiry across any AI channel or web form", "ClearLine AI Agent instantly analyzes intent & service fit", "Automated dialog answers questions & verifies availability", "Real-time calendar slot booked & synched to your CRM"]
    }
  }, {
    id: "reviews",
    title: "Automated Review & Reputation Loop",
    icon: MessageSquareCheck,
    description: "Automatically trigger SMS and email review requests upon service completion, turning happy clients into 5-star Google & AI engine citations.",
    manualState: {
      time: "Days or Weeks",
      cost: "Forgotten Follow-ups",
      errorRate: "90% Uncaptured Reviews",
      status: "Stagnant Search & AI Citations",
      steps: ["Job completes but staff forgets to send review request", "Delayed manual email sent days later with low response", "Unchecked negative sentiment leads to public bad reviews", "Zero structured data fed into local AI recommendation engines"]
    },
    automatedState: {
      time: "Immediate Post-Service",
      cost: "Automated Micro-Task",
      errorRate: "0% Forgotten Customers",
      status: "Continuous 5-Star Reputation Engine",
      steps: ["Job marked completed in CRM triggers instant automated request", "Smart sentiment check routes positive feedback to top review platforms", "Private feedback channel captures and resolves service issues early", "Reviews indexed dynamically to boost local AI search recommendations"]
    }
  }, {
    id: "crm",
    title: "AI Dispatch & CRM Synchronization",
    icon: CalendarCheck,
    description: "Eliminate manual data entry. Sync appointments, client notes, service tags, and team dispatch directly into your existing CRM or GoHighLevel system.",
    manualState: {
      time: "End of Day Batch",
      cost: "Administrative Friction",
      errorRate: "15% Duplicate / Missing Entries",
      status: "Fragmented Operations",
      steps: ["Paper notes or unorganized text messages saved manually", "Staff manually copies details into CRM after hours", "Incomplete client history causes miscommunication", "No automated SMS reminders sent prior to appointments"]
    },
    automatedState: {
      time: "Real-Time (Instant)",
      cost: "Zero Staff Time",
      errorRate: "0% Sync Discrepancies",
      status: "Unified Operational Command",
      steps: ["AI captures customer context, address, and job requirements", "Creates structured contact profile and updates pipeline stage instantly", "Triggers automated confirmation SMS and calendar calendar invites", "Prepares team dispatch notes with AI search query context"]
    }
  }];
  const currentWorkflow = workflows.find(w => w.id === activeTab) || workflows[0];
  const activeDetails = isAutomated ? currentWorkflow.automatedState : currentWorkflow.manualState;
  return <section data-section-id="3603" id="automationfeatures" className="bg-[#050507] text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16 relative overflow-hidden border-t border-[hsl(240_5%_15%)]">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[var(--accent-deep-red)]/10 blur-[140px] pointer-events-none rounded-lg" />

      <div className="max-w-7xl mx-auto relative z-10">
        {}
        <div ref={headerRef} className={`mb-16 md:mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-[hsl(240_5%_15%)] pb-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-block w-2 h-2 rounded-lg bg-[var(--accent-deep-red)] animate-pulse" />
                <span className="font-mono text-xs tracking-widest uppercase text-[var(--accent-deep-red)] font-semibold">
                  03 / SYSTEM AUTOMATION
                </span>
              </div>
              <h2 className="font-sans font-extrabold text-white tracking-tight leading-[1.05]" style={{
              fontSize: "clamp(2.25rem, 5vw, 4.25rem)"
            }}>
                Streamline customer acquisition with{" "}
                <span className="font-serif italic font-light text-[var(--accent-deep-red)]">
                  automated
                </span>{" "}
                intelligence
              </h2>
            </div>
            <p className="text-gray-400 max-w-md text-base leading-relaxed">
              Eliminate missed calls, slow replies, and cold leads. ClearLine builds custom 24/7 AI pipelines that capture, qualify, and book clients without human delay.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-16 md:mb-20">
          {metrics.map((metric, i) => {
          const IconComponent = metric.icon;
          return <div key={i} data-index={i} className="bg-[#0b0b0f] border border-[hsl(240_5%_15%)] p-6 md:p-8 rounded-xl shadow-lg relative group hover:border-[var(--accent-deep-red)]/50 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2.5 rounded-lg bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)]">
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <span className="font-mono text-[10px] text-gray-500 uppercase tracking-widest">
                    METRIC 0{i + 1}
                  </span>
                </div>
                <div className="font-sans font-extrabold text-3xl md:text-4xl text-white tracking-tight mb-1">
                  {metric.value}
                </div>
                <div className="font-semibold text-sm text-gray-200 mb-1">{metric.label}</div>
                <p className="text-xs text-gray-400 leading-normal">{metric.subtext}</p>
              </div>;
        })}
        </div>

        {}
        <div className="bg-[#0a0a0e] border border-[hsl(240_5%_15%)] rounded-xl p-6 md:p-10 shadow-2xl relative mb-16">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-8 border-b border-[hsl(240_5%_15%)]">
            {}
            <div className="flex flex-wrap items-center gap-2">
              {workflows.map((wf, i) => {
              const WfIcon = wf.icon;
              const isActive = activeTab === wf.id;
              return <button key={wf.id} data-index={i} onClick={() => setActiveTab(wf.id as "lead" | "reviews" | "crm")} className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${isActive ? "bg-[var(--accent-deep-red)] text-white shadow-md" : "bg-[#121218] text-gray-400 hover:text-white hover:bg-[#181822] border border-[hsl(240_5%_15%)]"}`}>
                    <WfIcon className="w-4 h-4" />
                    <span>{wf.title}</span>
                  </button>;
            })}
            </div>

            {}
            <div className="flex items-center gap-3 bg-[#121218] p-1.5 rounded-lg border border-[hsl(240_5%_15%)] self-start lg:self-auto">
              <button type="button" onClick={() => setIsAutomated(false)} className={`px-3.5 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-all duration-200 cursor-pointer ${!isAutomated ? "bg-zinc-800 text-zinc-300 border border-zinc-700 shadow-sm" : "text-gray-400 hover:text-gray-200"}`}>
                Manual Process
              </button>
              <button type="button" onClick={() => setIsAutomated(true)} className={`px-3.5 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-all duration-200 flex items-center gap-1.5 cursor-pointer ${isAutomated ? "bg-[var(--accent-deep-red)] text-white shadow-sm" : "text-gray-400 hover:text-gray-200"}`}>
                <Sparkles className="w-3.5 h-3.5" />
                ClearLine AI
              </button>
            </div>
          </div>

          {}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="inline-block px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] border border-[var(--accent-deep-red)]/20 mb-3">
                  {isAutomated ? "Automated Intelligence Active" : "Legacy Manual Mode"}
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                  {currentWorkflow.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {currentWorkflow.description}
                </p>
              </div>

              {}
              <div className="bg-[#111116] border border-[hsl(240_5%_15%)] p-5 rounded-xl space-y-3">
                <div className="flex items-center justify-between text-xs font-mono uppercase text-gray-400 pb-2 border-b border-[hsl(240_5%_15%)]">
                  <span>Performance Benchmark</span>
                  <span className={isAutomated ? "text-emerald-400" : "text-zinc-400"}>
                    {activeDetails.status}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <div>
                    <div className="text-[11px] text-gray-500 uppercase font-mono">Response Time</div>
                    <div className={`text-sm font-bold mt-0.5 ${isAutomated ? "text-emerald-400" : "text-zinc-400"}`}>
                      {activeDetails.time}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-500 uppercase font-mono">Error Rate</div>
                    <div className={`text-sm font-bold mt-0.5 ${isAutomated ? "text-emerald-400" : "text-zinc-400"}`}>
                      {activeDetails.errorRate}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-500 uppercase font-mono">Labor Cost</div>
                    <div className="text-sm font-bold text-gray-200 mt-0.5">
                      {activeDetails.cost}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="lg:col-span-7 bg-[#111116] border border-[hsl(240_5%_15%)] p-6 md:p-8 rounded-xl relative">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Workflow className="w-4 h-4 text-[var(--accent-deep-red)]" />
                  <span className="text-xs font-mono uppercase text-gray-300 font-semibold tracking-wider">
                    {isAutomated ? "ClearLine AI Execution Flow" : "Manual Operational Bottlenecks"}
                  </span>
                </div>
                <span className="text-[11px] font-mono text-gray-500">4 STAGE PIPELINE</span>
              </div>

              <div className="space-y-4 relative">
                {activeDetails.steps.map((stepText, idx) => <div key={idx} data-index={idx} className="flex items-start gap-4 p-4 rounded-lg bg-[#08080c] border border-[hsl(240_5%_12%)] transition-all duration-300 hover:border-[hsl(240_5%_20%)]">
                    <div className={`mt-0.5 flex items-center justify-center w-6 h-6 rounded-md text-xs font-bold shrink-0 ${isAutomated ? "bg-[var(--accent-deep-red)]/20 text-[var(--accent-deep-red)] border border-[var(--accent-deep-red)]/30" : "bg-zinc-800/60 text-zinc-400 border border-zinc-700/50"}`}>
                      {isAutomated ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono uppercase text-gray-500">
                          STAGE 0{idx + 1}
                        </span>
                        {isAutomated && <span className="text-[10px] font-mono text-emerald-400 bg-emerald-950/40 px-2 py-0.5 rounded-md border border-emerald-800/30">
                            AUTO-EXECUTED
                          </span>}
                      </div>
                      <p className="text-sm text-gray-200 mt-1 font-sans">{stepText}</p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="bg-gradient-to-r from-[#120709] via-[#0c0c10] to-[#070709] border border-[var(--accent-deep-red)]/30 p-8 md:p-12 rounded-xl flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--accent-deep-red)]/10 blur-2xl pointer-events-none rounded-lg" />
          
          <div className="space-y-2 max-w-2xl text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
              Ready to automate your local AI search pipeline?
            </h3>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed">
              Stop letting warm leads cool down. Get a tailored automation blueprint for your local service business today.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto shrink-0">
            <Link className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[var(--accent-deep-red)] text-white font-semibold text-sm hover:bg-[hsl(352_54%_40%)] transition-all duration-200 shadow-lg shadow-[var(--accent-deep-red)]/20" to="/PricingPlansPage"><span>get started now</span><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right w-4 h-4" aria-hidden="true"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>

            <Link to="/VisibilityCheckupPage" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#14141c] text-gray-200 font-semibold text-sm border border-[hsl(240_5%_20%)] hover:bg-[#1a1a24] hover:text-white transition-all duration-200">
              <span>Free AI Checkup</span>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}